import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { AddCircleOutline, ArrowDropDown, Edit } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Divider, Tooltip } from "@mui/material";
import { orderBy } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { UPGRADE_PLAN_TOOLTIP } from "src/utils/consts";
import { Document } from "../../../../../stores/models";
import {
  captureSentryException,
  DEFAULT_ACCEPTED_FILE_TYPES,
} from "../../../../../utils";
import { ConfirmationModal } from "../../../../ConfirmationModal";
import SelectableOption from "./SelectableOption";

const DocRevsionDropDown = ({
  docs,
  selectedDoc,
  isCreatingNewInstance,
  onCreateDocInstance,
  onSelectDocInstance,
  isDocEnabled,
  handleNewRecordFromFile,
  fileInput,
  standalone = false,
}: {
  docs?: Document[];
  selectedDoc: Document;
  onSelectDocInstance?: (doc: Document) => void;
  onCreateDocInstance?: () => void;
  isCreatingNewInstance?: boolean;
  isDocEnabled: boolean;
  handleNewRecordFromFile?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fileInput?: React.RefObject<HTMLInputElement>;
  standalone?: boolean;
}) => {
  const [docInstanceDropdownOpen, setDocInstanceDropdownOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const { docId } = useParams();
  const triggerShapeClass = standalone
    ? "rounded-md"
    : "rounded-l-md border-r-0";

  const sortedDocInstances = orderBy(docs, ["createdAt"], ["desc"]);
  const selectableDocInstances = sortedDocInstances.filter(
    (doc) => doc.versions.length > 0
  );
  const numberingSource = selectableDocInstances.some(
    (doc) => doc.id === selectedDoc.id
  )
    ? selectableDocInstances
    : sortedDocInstances;

  const getDocNumber = (doc: Document) => {
    const index = numberingSource.findIndex(
      (instance) => instance.id === doc.id
    );

    if (index < 0 || numberingSource.length === 0) {
      return 1;
    }

    return numberingSource.length - index;
  };

  const getRecordName = (doc: Document) =>
    doc.title.trim() || "Untitled record";

  const handleNewRecordClick = () => {
    setConfirmationModalOpen(true);
  };

  const handleNewRecordConfirm = () => {
    setConfirmationModalOpen(false);
    if (fileInput) {
      fileInput.current?.click();
      return;
    }

    if (onCreateDocInstance) {
      onCreateDocInstance();
    } else {
      captureSentryException(
        "Called handleNewRecordConfirm without handleNewRecordConfirm being defined"
      );
    }
  };

  const handleNewRecordCancel = () => {
    setConfirmationModalOpen(false);
  };

  return (
    <Tooltip
      title={UPGRADE_PLAN_TOOLTIP}
      {...(isDocEnabled && { open: false })}
    >
      <div>
        <ClickAwayListener
          onClickAway={() => setDocInstanceDropdownOpen(false)}
        >
          <div
            className={`relative ${docInstanceDropdownOpen ? "z-20" : ""}`}
            data-testid="doc-revision-dropdown"
          >
            <div
              className={`p-x-2 flex w-fit ${!isDocEnabled ? "pointer-events-none" : "cursor-pointer"} items-center justify-between ${triggerShapeClass} border border-solid border-gray-300 px-2 py-1.5 hover:bg-zinc-100`}
              onClick={() => {
                setDocInstanceDropdownOpen((prev) => !prev);
              }}
            >
              <div className="flex min-w-0 items-center gap-1">
                <SelectableOption
                  label={`Record #${getDocNumber(selectedDoc)}`}
                  disabled={!isDocEnabled}
                />
                <span
                  className="max-w-64 truncate text-sm"
                  title={getRecordName(selectedDoc)}
                >
                  - {getRecordName(selectedDoc)}
                </span>
              </div>
              <ArrowDropDown fontSize="small" />
            </div>

            <div
              className={`mt-1 flex  min-w-max flex-col overflow-auto rounded-md border border-solid border-slate-300 bg-color-bg shadow-md ${docInstanceDropdownOpen ? "absolute" : "hidden"}`}
            >
              {isDocEnabled &&
                selectableDocInstances.map((doc) => (
                  <div
                    className={`flex cursor-pointer items-center p-2 hover:bg-slate-100 ${docId === doc.id && "bg-slate-200"}`}
                    onClick={() => {
                      onSelectDocInstance && onSelectDocInstance(doc);
                      setDocInstanceDropdownOpen((prev) => !prev);
                    }}
                    key={`${doc.id}-selectable-option`}
                  >
                    <SelectableOption
                      label={`Record #${getDocNumber(doc)} - ${getRecordName(doc)} - ${moment(doc.createdAt).fromNow()}`}
                    />
                  </div>
                ))}
              <Divider />
              <>
                <LoadingButton
                  className="rounded-none"
                  startIcon={<AddCircleOutline />}
                  loading={isCreatingNewInstance}
                  onClick={handleNewRecordClick}
                >
                  New Record
                </LoadingButton>
                {handleNewRecordFromFile && fileInput && (
                  <input
                    id="hidden-qms-file-input"
                    type="file"
                    className="hidden"
                    accept={DEFAULT_ACCEPTED_FILE_TYPES}
                    max={1}
                    data-testid="hidden-qms-file-input"
                    ref={fileInput}
                    onChange={handleNewRecordFromFile}
                  />
                )}
                <ConfirmationModal
                  open={confirmationModalOpen}
                  onAbort={handleNewRecordCancel}
                  onConfirm={handleNewRecordConfirm}
                  content={
                    <>
                      You should only do this if you need a new instance of this
                      record.
                      <br />
                      If you want to make changes to the existing record you
                      should use the{" "}
                      <Edit fontSize="small" className="align-middle" /> button
                      on the right.
                    </>
                  }
                  title="New Record"
                  confirmText="New Record"
                />
              </>
            </div>
          </div>
        </ClickAwayListener>
      </div>
    </Tooltip>
  );
};

export default DocRevsionDropDown;
